import { StyleFn } from '..'

const COLUMNS = 12
const ORDERS = 6

const percentage = (index: number, count: number) => `${(index / count) * 100}%`

const up = (value: number) => `@media (min-width:${value}px)`

export const Row: StyleFn = ({ size }) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    // negative margins for use in a container
    width: `calc(100% + ${size.spacing[32]})`,
    margin: `0 calc(-${size.spacing[16]})`,
    '& $root': {
      width: '100%',
      margin: 0,
    },
  },
})

export const Col: StyleFn = ({ responsive }) => {
  // default for equal-width flexbox columns
  const root = {
    flexBasis: 0,
    flexGrow: 1,
    maxWidth: '100%',
  }

  // commmon properties for all breakpoints
  const common = {
    position: 'relative',
    // Prevent columns from becoming too narrow when at smaller grid tiers by
    // always setting `width: 100%;`. This works because we use `flex` values
    // later on to override this initial width.
    width: '100%',
    // Prevent columns from collapsing when empty
    minHeight: 1,
  }

  const auto = {
    flex: '0 0 auto',
    width: 'auto',
    maxWidth: 'none', // Reset earlier grid tiers
  }

  const makeColumns = () => {
    let styles: any = {}
    let mqStyles: any = {}

    // for each breakpoint
    Object.keys(responsive.breakpoint).map((bp: string) => {
      const smallestBreakpoint = bp === 'xxs'
      const autoRule = smallestBreakpoint ? 'auto' : `${bp}-auto`
      styles[autoRule] = { ...common }

      // for each column
      for (let i = 1; i < COLUMNS + 1; i++) {
        const colRule = smallestBreakpoint ? `${i}` : `${bp}-${i}`
        const columnStyles = {
          flex: `0 0 ${percentage(i, COLUMNS)}`,
          // Add a `max-width` to ensure content within each column does not blow out
          // the width of the column. Applies to IE10+ and Firefox. Chrome and Safari
          // do not appear to require this.
          maxWidth: `${percentage(i, COLUMNS)}`,
        }

        if (smallestBreakpoint) {
          styles[colRule] = { ...common, ...columnStyles }
        } else {
          styles[colRule] = common
          mqStyles[up((responsive.breakpoint as any)[bp])] = {
            ...mqStyles[up((responsive.breakpoint as any)[bp])],
            [bp]: root,
            [colRule]: columnStyles,
            [autoRule]: auto,
          }
        }
      }
    })
    return { ...styles, ...mqStyles }
  }

  return {
    root,
    ...makeColumns(),
  }
}

export const Order = (theme: any): any => {
  const { responsive } = theme
  const first = { order: -1 }
  const last = { order: 6 }
  const styles: any = { first, last }
  const mqStyles: any = {}

  // for each breakpoint
  Object.keys(responsive.breakpoint).map((bp: string) => {
    const smallestBreakpoint = bp === 'xxs'

    // for each order priority
    for (let i = 0; i < ORDERS; i++) {
      const rule = smallestBreakpoint ? `${i}` : `${bp}-${i}`
      if (smallestBreakpoint) {
        styles[rule] = { order: i }
      } else {
        mqStyles[up(responsive.breakpoint[bp])] = {
          ...mqStyles[up(responsive.breakpoint[bp])],
          [rule]: { order: i },
          [`${bp}-first`]: first,
          [`${bp}-last`]: last,
        }
      }
    }
  })
  return { ...styles, ...mqStyles }
}

export const Offset = (theme: any): any => {
  const { responsive } = theme
  const styles: any = {}
  const mqStyles: any = {}

  // for each breakpoint
  Object.keys(responsive.breakpoint).map((bp: string) => {
    const smallestBreakpoint = bp === 'xxs'

    // for each column
    for (let i = 0; i < COLUMNS; i++) {
      const rule = smallestBreakpoint ? `${i}` : `${bp}-${i}`
      if (smallestBreakpoint) {
        if (i > 0) styles[rule] = { marginLeft: percentage(i, COLUMNS) }
      } else {
        mqStyles[up(responsive.breakpoint[bp])] = {
          ...mqStyles[up(responsive.breakpoint[bp])],
          [rule]: { marginLeft: percentage(i, COLUMNS) },
        }
      }
    }
  })
  return { ...styles, ...mqStyles }
}
